import React, { createContext, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { ShopConfig, easyleaseConfig } from '../data/shopConfig';
import { WidgetConfig, easyleaseWidget } from '../data/widgetConfig';
import { proposeBrandTheme } from '../utils/theme';
import { useBusiness } from '../components/BusinessContext';

/**
 * The merchant's DRAFT configuration for both customer-facing surfaces — their
 * Talab shop page and their embeddable website widget. It lives above both the
 * Studio and Settings so the two never drift apart, and so undo/redo spans the
 * whole draft rather than one screen.
 */
interface Draft {
  shop: ShopConfig;
  widget: WidgetConfig;
}
interface ShopStudioValue {
  config: ShopConfig;
  widget: WidgetConfig;
  update: (patch: Partial<ShopConfig>) => void;
  updateWidget: (patch: Partial<WidgetConfig>) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  resetTheme: () => void;
  toast: (message: string) => void;
  /** True when edits have been made since the shop was last published. */
  dirty: boolean;
  markPublished: () => void;
}
const ShopStudioContext = createContext<ShopStudioValue | null>(null);
export function useShopStudio(): ShopStudioValue {
  const ctx = useContext(ShopStudioContext);
  if (!ctx) throw new Error('useShopStudio must be used inside a ShopStudioProvider');
  return ctx;
}
export function ShopStudioProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    hub
  } = useBusiness();
  const [draft, setDraft] = useState<Draft>(() => ({
    shop: {
      ...easyleaseConfig,
      publicName: hub?.name ?? easyleaseConfig.publicName,
      handle: hub?.shopSlug ?? easyleaseConfig.handle
    },
    widget: easyleaseWidget
  }));
  const [past, setPast] = useState<Draft[]>([]);
  const [future, setFuture] = useState<Draft[]>([]);
  const [message, setMessage] = useState('');
  const [dirty, setDirty] = useState(false);

  /** Every change is one history entry covering the whole draft. */
  const commit = (next: (d: Draft) => Draft) => {
    setPast(p => [...p.slice(-24), draft]);
    setFuture([]);
    setDirty(true);
    setDraft(next);
  };
  const update = (patch: Partial<ShopConfig>) => commit(d => ({
    ...d,
    shop: {
      ...d.shop,
      ...patch
    }
  }));
  const updateWidget = (patch: Partial<WidgetConfig>) => commit(d => ({
    ...d,
    widget: {
      ...d.widget,
      ...patch
    }
  }));
  const undo = () => {
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    setPast(p => p.slice(0, -1));
    setFuture(f => [draft, ...f]);
    setDraft(previous);
  };
  const redo = () => {
    if (future.length === 0) return;
    const next = future[0];
    setFuture(f => f.slice(1));
    setPast(p => [...p, draft]);
    setDraft(next);
  };
  const resetTheme = () => {
    const brand = proposeBrandTheme('logo');
    commit(d => ({
      ...d,
      shop: {
        ...d.shop,
        theme: {
          ...d.shop.theme,
          preset: 'auto',
          primary: brand.primary,
          accent: brand.accent,
          background: brand.background,
          backgroundStyle: 'tint',
          backdrop: 'plain',
          backgroundStrength: 45,
          useBrandAutomatically: true,
          chatButtonColor: '',
          chatAccentColor: '',
          chatBackground: 'white'
        }
      }
    }));
  };
  const toast = (text: string) => {
    setMessage(text);
    window.setTimeout(() => setMessage(''), 2400);
  };
  return <ShopStudioContext.Provider value={{
    config: draft.shop,
    widget: draft.widget,
    update,
    updateWidget,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    resetTheme,
    toast,
    dirty,
    markPublished: () => setDirty(false)
  }} data-id="element-5235" data-mp="275bc5um8deoq">
      {children}
      <AnimatePresence data-id="element-5236" data-mp="a85jn4b9djor">
        {message && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-4 py-2.5 bg-dark-navy text-white rounded-full text-sm font-semibold shadow-large inline-flex items-center gap-2" data-id="element-5237" data-mp="a7dmvq5xdfww">
            <CheckIcon className="w-4 h-4 text-success" data-id="element-5238" data-mp="h16ma5588wk" />
            {message}
          </motion.div>}
      </AnimatePresence>
    </ShopStudioContext.Provider>;
}